import { useState, useRef, useMemo, useEffect } from "react";
//import { languages } from "../../utils/languages";
import { googleSupportedLanguages } from '../../services/languageService';
import useOnClickOutside from "../../utils/useOnClickOutside";
import { convertToArrayOfObjects } from "../../lib/utils";
import downIcon from '../../assets/icons/down_icon.svg';

function SelectWithSearch({
    options,
    onChange,
    inputPlaceholder,
    isStringArray,
    isSearch, selectPlaceholder,
    defaultValue = "",
    value: controlledValue,
    onDropdownOpenChange,
    isSourceAutoDetected,
    isTargetAutoDetected
}) {
    const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue);
    const [search, setSearch] = useState("");
    const [isHidden, setIsHidden] = useState(true);
    const [showSearch, setShowSearch] = useState(false);
    const selectRef = useRef(null);
    const optionsRef = useRef(null);
    const inputRef = useRef(null);
    useOnClickOutside([optionsRef, selectRef], () => closeOptions());

    const value = typeof controlledValue !== 'undefined' ? controlledValue : uncontrolledValue;
    useEffect(() => {
        if (typeof controlledValue === 'undefined') {
            setUncontrolledValue(defaultValue);
        }
    }, [defaultValue, controlledValue]);

    useEffect(() => {
        if (!isHidden) {
            setTimeout(() => {
                if (isSearch && inputRef.current) {
                    inputRef.current.focus();
                } else if (optionsRef.current) {
                    optionsRef.current.focus();
                }
            }, 0);
        }
    }, [isHidden, isSearch]);

    const openOptions = () => {
        setIsHidden(false);
        setShowSearch(true);
        setSearch("");
        setTimeout(() => {
            inputRef.current?.focus();
        }, 0);
    };

    const closeOptions = () => {
        setIsHidden(true);
        setShowSearch(false);
        setSearch("");
    };

    const handleSelectValue = (code) => {
        if (isStringArray) {
            onChange(code);
            if (typeof controlledValue === 'undefined') {
                setUncontrolledValue(code);
            }
        } else {
            // Find the language object to get the name
            const selectedLang = googleSupportedLanguages.find(lang => lang.code === code);
            const valueToPass = selectedLang ? selectedLang.name : code;

            onChange(valueToPass);
            if (typeof controlledValue === 'undefined') {
                setUncontrolledValue(valueToPass);
            }
        }
        closeOptions();
    }

    const filteredOptions = useMemo(() => {
        const optionsArray = isStringArray ? convertToArrayOfObjects(options) : options;
        if (!search) return optionsArray;
        return optionsArray.filter(item =>
            item.name.toLowerCase().includes(search.toLowerCase())
        );
    }, [search, options, isStringArray]);

    const highlightMatch = (text, query) => {
        if (!query) return text;
        const parts = text.split(new RegExp(`(${query})`, 'gi'));
        return parts.map((part, i) =>
            part.toLowerCase() === query.toLowerCase() ?
                <span key={i} className="font-bold">{part}</span> :
                part
        );
    };

    const renderOptions = () => {
        if (filteredOptions.length === 0 && search.trim()) {
            return (
                <div
                    className="mt-[2px] py-2 px-3 text-[14px] leading-[20px] font-normal cursor-pointer hover:bg-[#F1F5F9] hover:rounded-[4px] hover:font-medium"
                    onClick={() => handleSelectValue(search)}
                >
                    {search}
                </div>
            );
        }
        return filteredOptions.map((item, i) => {
            const { code, name } = item;
            const isActive = value === name || ((isSourceAutoDetected || isTargetAutoDetected) && code === 'detect');
            const isDetectLanguage = code === 'detect';
            return (
                <div
                    key={i}
                    className={`mt-[2px] py-2 px-3 text-[14px] leading-[20px] font-normal cursor-pointer hover:bg-[#F1F5F9] hover:rounded-[4px] hover:font-medium ${isActive && "font-medium bg-[#F1F5F9]"} ${isDetectLanguage ? "font-bold text-blue-600 border-b border-gray-200" : ""}`}
                    onClick={() => handleSelectValue(code)}
                >
                    {highlightMatch(name, search)}
                </div>
            );
        });
    };

    const handleSearchKeyDown = (e) => {
        if (e.key === 'Enter' && search.trim()) {
            handleSelectValue(search);
        }
    };

    const selectValue = value === 'Detect language' ? "" :
        (isStringArray ? value : googleSupportedLanguages.find(l => l.code === value)?.name || value);

    const handleDropdownKeyDown = (e) => {
        if (e.key === 'Escape') {
            e.stopPropagation();
            closeOptions();
        }
    };

    return <div className="relative">
        <div ref={selectRef} onClick={() => isHidden ? openOptions() : closeOptions()} className="bg-[#fff] cursor-pointer flex py-2 px-3 items-center border-[#E2E8F0] border [box-shadow:0_1px_2px_0_rgba(0,_0,_0,_0.05)] rounded-[6px]">
            <div className="flex items-center w-full">
                {showSearch ? (
                    <input
                        ref={inputRef}
                        type="text"
                        value={search}
                        onKeyDown={handleSearchKeyDown}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={inputPlaceholder}
                        className="w-full text-[14px] leading-[20px] focus:outline-none"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (isHidden) {
                                openOptions();
                            }
                        }}
                    />
                ) : (
                    <p className={`w-full text-[14px] leading-[20px] ${value && value !== 'Detect language' ? "" : "text-[#6B7280]"}`}>
                        {selectValue || selectPlaceholder}
                    </p>
                )}
            </div>
            <img src={downIcon} alt="Down icon" className="h-4 w-4" />
        </div>
        <div ref={optionsRef} className={`${isHidden ? "hidden" : ""} w-full absolute z-40 bg-[#fff] rounded-[8px] border border-[#E2E8F0] mt-[2px] [box-shadow:0_1px_3px_0_rgba(0,_0,_0,_0.1)]`} tabIndex={0} onKeyDown={handleDropdownKeyDown}>
            <div className="max-h-[388px] overflow-y-auto [&::-webkit-scrollbar]:w-[2px] [&::-webkit-scrollbar-thumb]:bg-[#00000066] [&::-webkit-scrollbar-thumb]:h-[28px] [&::-webkit-scrollbar-thumb]:rounded">
                {renderOptions()}
            </div>
        </div>
    </div>
}

export default SelectWithSearch;